import React, { useRef, useState, useEffect, useCallback } from "react";
import { CgUndo } from "react-icons/cg";
import { FaRegCircle } from "react-icons/fa";
import { HiPencil } from "react-icons/hi2";

const DEFAULT_COLOR = "#ef4444";

/**
 * Issue-log specific annotation canvas. Calls onConfirm(dataUrl, issueLog)
 * where issueLog = { coordinates: [...], markup: number } for the upload API.
 */
export default function IssueLogAnnotationCanvas({ imageSrc, onConfirm, onBack }) {
  const canvasRef = useRef(null);
  const [drawings, setDrawings] = useState([]);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [currentShape, setCurrentShape] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const selectedColor = DEFAULT_COLOR;
  const [selectedTool, setSelectedTool] = useState("pen");
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = imageSrc;
  }, [imageSrc]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const scale = Math.min(
      canvas.width / image.width,
      canvas.height / image.height
    );
    const x = (canvas.width - image.width * scale) / 2;
    const y = (canvas.height - image.height * scale) / 2;
    ctx.drawImage(image, x, y, image.width * scale, image.height * scale);

    for (const item of drawings) {
      if (item.kind === "stroke") {
        const stroke = item.data;
        if (stroke.points.length < 2) continue;
        ctx.beginPath();
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
        ctx.stroke();
      } else {
        const shape = item.data;
        ctx.strokeStyle = shape.color;
        ctx.lineWidth = 3;
        if (shape.type === "circle") {
          const rx = Math.abs(shape.endX - shape.startX) / 2;
          const ry = Math.abs(shape.endY - shape.startY) / 2;
          const cx = shape.startX + (shape.endX - shape.startX) / 2;
          const cy = shape.startY + (shape.endY - shape.startY) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          ctx.strokeRect(
            Math.min(shape.startX, shape.endX),
            Math.min(shape.startY, shape.endY),
            Math.abs(shape.endX - shape.startX),
            Math.abs(shape.endY - shape.startY)
          );
        }
      }
    }

    if (currentStroke.length > 1) {
      ctx.beginPath();
      ctx.strokeStyle = selectedColor;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.moveTo(currentStroke[0].x, currentStroke[0].y);
      for (let i = 1; i < currentStroke.length; i++) {
        ctx.lineTo(currentStroke[i].x, currentStroke[i].y);
      }
      ctx.stroke();
    }

    if (currentShape) {
      ctx.strokeStyle = currentShape.color;
      ctx.lineWidth = 3;
      if (currentShape.type === "circle") {
        const rx = Math.abs(currentShape.endX - currentShape.startX) / 2;
        const ry = Math.abs(currentShape.endY - currentShape.startY) / 2;
        const cx =
          currentShape.startX + (currentShape.endX - currentShape.startX) / 2;
        const cy =
          currentShape.startY + (currentShape.endY - currentShape.startY) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeRect(
          Math.min(currentShape.startX, currentShape.endX),
          Math.min(currentShape.startY, currentShape.endY),
          Math.abs(currentShape.endX - currentShape.startX),
          Math.abs(currentShape.endY - currentShape.startY)
        );
      }
    }
  }, [image, drawings, currentStroke, currentShape, selectedColor]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    setIsDrawing(true);
    const pos = getPos(e);
    if (selectedTool === "pen") {
      setCurrentStroke([pos]);
    } else {
      setCurrentShape({
        type: selectedTool,
        startX: pos.x,
        startY: pos.y,
        endX: pos.x,
        endY: pos.y,
        color: selectedColor,
      });
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const pos = getPos(e);
    if (selectedTool === "pen") {
      setCurrentStroke((prev) => [...prev, pos]);
    } else if (currentShape) {
      setCurrentShape({ ...currentShape, endX: pos.x, endY: pos.y });
    }
  };

  const endDraw = () => {
    if (selectedTool === "pen" && currentStroke.length > 1) {
      setDrawings((prev) => [
        ...prev,
        {
          kind: "stroke",
          data: { points: currentStroke, color: selectedColor },
        },
      ]);
    } else if (currentShape) {
      setDrawings((prev) => [...prev, { kind: "shape", data: currentShape }]);
    }
    setCurrentStroke([]);
    setCurrentShape(null);
    setIsDrawing(false);
  };

  const undo = () => setDrawings((prev) => prev.slice(0, -1));

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const coordinates = drawings.map((item) => {
      if (item.kind === "stroke") {
        return {
          type: "stroke",
          points: item.data.points,
          color: item.data.color,
        };
      }
      const shape = item.data;
      return {
        type: shape.type,
        startX: shape.startX,
        startY: shape.startY,
        endX: shape.endX,
        endY: shape.endY,
        color: shape.color,
      };
    });
    const issueLog = { coordinates, markup: coordinates.length };
    onConfirm?.(dataUrl, issueLog);
  };

  const tools = [
    { id: "pen", icon: HiPencil, label: "Pen" },
    { id: "circle", icon: FaRegCircle, label: "Circle" },
  ];

  const toolBtn = (active) => ({
    padding: 8,
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    background: active ? "#4D6BF6" : "transparent",
    color: active ? "#fff" : "#6b7280",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "8px 16px",
          borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 4, paddingRight: 16 }}>
          {tools.map((tool) => (
            <button
              key={tool.id}
              type="button"
              onClick={() => setSelectedTool(tool.id)}
              style={toolBtn(selectedTool === tool.id)}
              title={tool.label}
            >
              <tool.icon size={20} />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={undo}
          disabled={drawings.length === 0}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 10px",
            border: "none",
            background: "transparent",
            cursor: drawings.length === 0 ? "not-allowed" : "pointer",
            opacity: drawings.length === 0 ? 0.5 : 1,
            color: "#374151",
            fontSize: 13,
          }}
        >
          <CgUndo size={18} /> Undo
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, background: "#f8fafc", position: "relative" }}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", cursor: "crosshair" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            background: "#fff",
            color: "#374151",
            cursor: "pointer",
          }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={
            drawings.length === 0 && !currentShape && currentStroke.length === 0
          }
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            border: "none",
            background: "#0d0d0d",
            color: "#fff",
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "Inter Tight",
            cursor:
              drawings.length === 0 && !currentShape && currentStroke.length === 0
                ? "not-allowed"
                : "pointer",
            opacity:
              drawings.length === 0 && !currentShape && currentStroke.length === 0
                ? 0.5
                : 1,
          }}
        >
          Confirm Annotation
        </button>
      </div>
    </div>
  );
}
